import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useAuth } from '../../context/AuthContext';
import { useSession } from '../../context/SessionContext';
import {
  OnboardingApiError,
  fetchAssignedPaths,
  fetchGeneratedPlan,
  fetchOnboardingMissions,
  fetchPathDetail,
} from './api';
import {
  buildAssignedPath,
  buildGeneratedPath,
  buildMissionPath,
  claimedMissionIds,
  findNextStep,
} from './model';
import { readProgress, toggleCompletion, writeProgress } from './progress';

/**
 * Data layer for the Onboarding surface (spec §9.4, §10.4, §11).
 *
 * Load order matters for the recovery contract: local progress is read
 * synchronously on mount, so a failed or slow network never shows the user an
 * empty path or a reset progress bar. The server data then layers evidence on
 * top of that (mission mastery, deck review state) — it never erases it.
 *
 * Assigned paths are fetched as a list first (cheap, one query) and their
 * chapters are then loaded per path. A detail request that fails degrades that
 * one path to "summary only" instead of failing the whole screen.
 */
export function useOnboarding() {
  const { token } = useAuth();
  const { sessionId, workspacePath, isConnected, isRestoring } = useSession();

  const [stored, setStored] = useState(() => readProgress(workspacePath));
  const [assigned, setAssigned] = useState([]);
  const [details, setDetails] = useState({}); // pathId -> {status, modules}
  const [missions, setMissions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [generating, setGenerating] = useState(false);
  const [generationError, setGenerationError] = useState(null);

  const abortRef = useRef(null);
  const generationAbortRef = useRef(null);

  // Re-read the store when the knowledge source changes: plans and completions
  // are scoped per source, and mixing them would mis-report progress.
  useEffect(() => {
    setStored(readProgress(workspacePath));
  }, [workspacePath]);

  // Connection state is NOT tracked here — `OfflineNotice`/`useOnlineStatus`
  // own it, and they can actually probe the backend rather than trusting
  // `navigator.onLine`. This hook only reports what the requests did.

  const load = useCallback(async () => {
    abortRef.current?.abort();
    const controller = new AbortController();
    abortRef.current = controller;
    const { signal } = controller;

    setLoading(true);
    setError(null);

    try {
      const [pathsResult, missionsResult] = await Promise.allSettled([
        fetchAssignedPaths({ token, signal }),
        fetchOnboardingMissions({ token, workspacePath, signal }),
      ]);
      if (signal.aborted) return;

      // A missing mission list is survivable — a missing path list is not,
      // because that is the required half of the screen.
      if (pathsResult.status === 'rejected') {
        if (pathsResult.reason?.name === 'AbortError') return;
        throw pathsResult.reason;
      }

      const summaries = Array.isArray(pathsResult.value?.paths) ? pathsResult.value.paths : [];
      setAssigned(summaries);
      setMissions(
        missionsResult.status === 'fulfilled' && Array.isArray(missionsResult.value?.missions)
          ? missionsResult.value.missions
          : []
      );
      setLoading(false);

      // Chapters, one request per path. Failures degrade that path only.
      const loaded = {};
      await Promise.all(
        summaries.map(async (summary) => {
          try {
            const detail = await fetchPathDetail(summary.id, { token, signal });
            loaded[summary.id] = {
              status: 'ready',
              modules: Array.isArray(detail?.modules) ? detail.modules : [],
            };
          } catch (err) {
            if (err?.name !== 'AbortError') loaded[summary.id] = { status: 'failed', modules: [] };
          }
        })
      );
      if (!signal.aborted) setDetails(loaded);
    } catch (err) {
      if (err?.name === 'AbortError') return;
      setError(err instanceof OnboardingApiError ? err : new OnboardingApiError(err?.message || 'failed'));
      setLoading(false);
    }
  }, [token, workspacePath]);

  useEffect(() => {
    load();
    return () => abortRef.current?.abort();
  }, [load]);

  useEffect(() => () => generationAbortRef.current?.abort(), []);

  /** Ask the planner for a voluntary path over the connected source. */
  const generate = useCallback(async () => {
    if (!sessionId) return;
    generationAbortRef.current?.abort();
    const controller = new AbortController();
    generationAbortRef.current = controller;

    setGenerating(true);
    setGenerationError(null);
    try {
      const data = await fetchGeneratedPlan(sessionId, { token, signal: controller.signal });
      const steps = Array.isArray(data?.result) ? data.result : [];
      if (steps.length === 0) {
        setGenerationError(new OnboardingApiError('empty plan', 200, 'generation_empty'));
        return;
      }
      // Regenerating replaces the plan, so the completions that pointed at the
      // old step ids are dropped with it rather than left dangling.
      const next = writeProgress(workspacePath, {
        plan: steps,
        planCreatedAt: new Date().toISOString(),
        completed: {},
        lastStepId: null,
      });
      setStored(next);
    } catch (err) {
      if (err?.name === 'AbortError') return;
      setGenerationError(err instanceof OnboardingApiError ? err : new OnboardingApiError(err?.message || 'failed'));
    } finally {
      setGenerating(false);
    }
  }, [sessionId, token, workspacePath]);

  /** Flip a self-reported step. Written through to storage immediately. */
  const setStepDone = useCallback(
    (stepId, done) => {
      const completed = toggleCompletion(workspacePath, stepId, done);
      setStored((prev) => ({ ...prev, completed, lastStepId: stepId }));
    },
    [workspacePath]
  );

  /** Remember which path the user was in, so a return trip lands there. */
  const rememberPath = useCallback(
    (pathId) => {
      writeProgress(workspacePath, { lastPathId: pathId });
      setStored((prev) => ({ ...prev, lastPathId: pathId }));
    },
    [workspacePath]
  );

  const paths = useMemo(() => {
    const built = assigned.map((summary) =>
      buildAssignedPath({
        summary,
        detail: details[summary.id],
        missions,
        completed: stored.completed,
      })
    );

    const generated = buildGeneratedPath({ steps: stored.plan, missions, completed: stored.completed });
    if (generated) built.push(generated);

    const leftovers = buildMissionPath({ missions, claimedIds: claimedMissionIds(built) });
    if (leftovers) built.push(leftovers);

    return built;
  }, [assigned, details, missions, stored.completed, stored.plan]);

  const next = useMemo(() => findNextStep(paths, stored.lastStepId), [paths, stored.lastStepId]);

  const totals = useMemo(() => {
    let total = 0;
    let completed = 0;
    let requiredTotal = 0;
    let requiredCompleted = 0;
    for (const path of paths) {
      total += path.progress.total;
      completed += path.progress.completed;
      requiredTotal += path.progress.requiredTotal;
      requiredCompleted += path.progress.requiredCompleted;
    }
    return { total, completed, requiredTotal, requiredCompleted };
  }, [paths]);

  return {
    paths,
    next,
    totals,
    loading: loading || isRestoring,
    error,
    generating,
    generationError,
    // Planning needs a live session behind it; without one the button explains
    // itself instead of failing on click.
    canGenerate: Boolean(sessionId && isConnected),
    hasPlan: Array.isArray(stored.plan) && stored.plan.length > 0,
    lastPathId: stored.lastPathId,
    reload: load,
    generate,
    setStepDone,
    rememberPath,
  };
}

export default useOnboarding;
